import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLocation } from "wouter";
import ClarityLogo from "@/assets/clarity-logo-light.svg";

const CLARITY_LOGO_URL = ClarityLogo;

export default function ResetPassword() {
  const [, setLocation] = useLocation();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) setToken(t);
  }, []);

  const confirmReset = trpc.auth.confirmPasswordReset.useMutation({
    onSuccess: () => {
      setDone(true);
    },
    onError: (err) => toast.error(err.message || "Reset failed. The link may have expired."),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (!token) {
      toast.error("Invalid reset link. Please request a new one.");
      return;
    }
    confirmReset.mutate({ token, newPassword });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <header className="border-b border-gray-100 px-8 py-5 flex items-center justify-between">
        <img src={CLARITY_LOGO_URL} alt="Clarity" className="h-7 object-contain" />
        <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-medium">Service Portal</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center">
          <img src={CLARITY_LOGO_URL} alt="Clarity" className="h-10 object-contain mx-auto mb-8" />
          <div className="w-8 h-px bg-black mx-auto mb-8" />

          {done ? (
            <>
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Password Updated</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
              <button
                onClick={() => setLocation("/login")}
                className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors"
              >
                Sign In
              </button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-light tracking-wide text-black mb-2">Set New Password</h1>
              <p className="text-sm text-gray-400 leading-relaxed mb-10">
                Choose a new password for your account. Minimum 6 characters.
              </p>
              {!token && (
                <p className="text-sm text-red-500 mb-6">
                  Invalid reset link. Please request a new password reset from the login page.
                </p>
              )}
              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">New Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 characters"
                      required
                      minLength={6}
                      className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none pr-12"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors text-xs"
                      tabIndex={-1}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    required
                    className="w-full border border-gray-200 px-4 py-3 text-sm text-black placeholder-gray-300 focus:outline-none focus:border-black transition-colors rounded-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={confirmReset.isPending || !newPassword || !confirmPassword || !token}
                  className="w-full bg-black text-white h-12 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirmReset.isPending ? "Updating…" : "Update Password"}
                </button>
              </form>
              <button
                type="button"
                onClick={() => setLocation("/login")}
                className="mt-6 text-xs text-gray-400 hover:text-black transition-colors"
              >
                ← Back to sign in
              </button>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-gray-100 px-8 py-4 flex items-center justify-between">
        <p className="text-xs text-gray-300">&copy; {new Date().getFullYear()} Clarity. All rights reserved.</p>
        <p className="text-xs text-gray-300 tracking-[0.15em] uppercase">Clarity®</p>
      </footer>
    </div>
  );
}
